import { computed, ref } from 'vue'
import { useRouteQuery } from '@vueuse/router'
import useRef from '@nicolaudie-group/ng-ui-kit/composables/useRef'
import usePagination from '@nicolaudie-group/ng-ui-kit/composables/usePagination'
import {
  ALL_LIMITS,
  DEFAULT_LIMIT,
  getRoute,
  limitOptions,
} from './helpers'
import useAutofix from './useAutofix'

/**
 * Make sure we always get a number, instead of strings or undefined
 */
function parseNumberParam(value, fallback = 1) {
  const result = parseInt(String(value), 10)
  return Number.isNaN(result) ? fallback : result
}

function createFixLimitParam(defaultLimit = DEFAULT_LIMIT) {
  return (value) => {
    const num = parseNumberParam(value, defaultLimit)
    const result = ALL_LIMITS.includes(num) ? num : defaultLimit
    return result
  }
}

function usePaginationParams(count, loaded, {
  query = {},
  loading = false,
  defaultLimit = DEFAULT_LIMIT,
} = {}) {
  const {
    enabled: queryEnabled = false,
    mode: queryMode,
  } = query

  const loadingRef = useRef(loading)

  const fixLimitParam = createFixLimitParam(defaultLimit)

  function getLimitRoute(route, newLimit) {
    return getRoute(route, { limit: newLimit, page: undefined })
  }

  const limit = queryEnabled
    ? useRouteQuery('limit', defaultLimit, {
      mode: queryMode,
      transform: { get: fixLimitParam, set: fixLimitParam },
    })
    : ref(defaultLimit)

  const page = queryEnabled
    ? useRouteQuery('page', 1, {
      mode: queryMode,
      transform: parseNumberParam,
    })
    : ref(1)

  if (!queryEnabled) {
    useAutofix(limit, fixLimitParam)
  }

  const pagination = usePagination(count, {
    limit,
    page,
    // Autofix page number only when the list was loaded, so that
    // we know the maximum number of pages
    autofix: loaded,
  })

  function applyLimit(newLimit) {
    limit.value = newLimit
    page.value = 1
  }

  function loadPage(newPage) {
    // Disable pagination when loading
    if (!loadingRef.value) {
      pagination.goto(newPage)
    }
  }

  // const route = useRoute()

  const paginationProps = computed(() => ({
    footer: {
      router: queryEnabled,
      replace: queryEnabled ? queryMode === 'replace' : undefined,
      readonly: loadingRef.value,
      maxPages: 5,
      pageCount: pagination.lastPage.value,
      currentPage: page.value,
      onGoto: loadPage,
    },
    limit: {
      value: limit.value,
      options: limitOptions,
      disabled: loadingRef.value,
      onInput: applyLimit,
    },
  }))

  const paginationText = computed(() => `${pagination.from.value}-${pagination.to.value} / ${pagination.numItems.value}`)

  return {
    ...pagination,
    page,
    limit,
    applyLimit,
    getLimitRoute,
    paginationProps,
    paginationText,
  }
}

export default usePaginationParams
